import { Injectable } from '@angular/core';
import { AuthService } from '../../../auth/auth.service';
import { UserService } from '../../../operator/user-service/user.service';
import { distinctUntilChanged, filter, take } from 'rxjs/operators';
import { MultiBuyerCmp } from '../../interfaces/company.interface';
import { CompanyService } from '../company/company.service';
import { MultiBuyerStateService } from './multi-buyer-state.service';

export class NullMultiBuyerCmp implements MultiBuyerCmp {
  readonly id = 0;
  readonly userId = 0;
  readonly nm = '';
  readonly apiUserId = 0;
}

@Injectable({
  providedIn: 'root',
})
export class MultiBuyerSession {
  constructor(
    private readonly _userService: UserService,
    private readonly _companyService: CompanyService,
    private readonly _auth: AuthService,
    private readonly _multiBuyerState: MultiBuyerStateService
  ) {
    this._multiBuyerState.loggedInAsMultiBuyer$
      .pipe(
        distinctUntilChanged(),
        filter(
          (status) =>
            !!status &&
            !!this._auth.user &&
            this._multiBuyerState.accessControlConfig.accessibleEntities
              .length > 0
        )
      )
      .subscribe(() => {
        this.selectPrimaryEntityPromise();
      });
  }

  public async buyers(
    cmpId: number,
    buyerCompanyId?: number
  ): Promise<MultiBuyerCmp[]> {
    const userRole = this._multiBuyerState.accessControlConfig.userRole;
    const allBuyers =
      (await this._companyService
        .getMultiBuyerCmpsList(cmpId, buyerCompanyId, userRole)
        .pipe(take(1))
        .toPromise()) ?? [];

    const accessibleIds = this._multiBuyerState.multiBuyerCmpsIds;
    if (!accessibleIds.length) {
      return allBuyers;
    }
    return allBuyers.filter((b) => accessibleIds.includes(b.id));
  }

  public async selectBuyer(
    cmpId: number,
    buyerCompanyId: number
  ): Promise<MultiBuyerCmp> {
    const buyers = await this.buyers(cmpId, buyerCompanyId);
    if (buyers.length === 0) {
      throw new Error('No buyer company found for the provided IDs.');
    }
    const selectedCompany = buyers.find((item) => item.id === buyerCompanyId);
    if (!selectedCompany) {
      throw new Error(
        `Buyer company ${buyerCompanyId} not found in the returned list.`
      );
    }

    const tokenResponse = await this._userService
      .getToken(selectedCompany.apiUserId, false)
      .toPromise();

    if (tokenResponse) {
      const multiBuyerCmpAuth = {
        ...tokenResponse,
        ...selectedCompany,
        parentCompany: cmpId,
      };

      this._auth.setSelectedMultiBuyerCompanyData(
        multiBuyerCmpAuth,
        selectedCompany.id
      );
    }
    return selectedCompany;
  }

  public resetBuyerToken(): void {
    this._auth.resetMultiBuyerToken();
  }

  public async selectPrimaryEntityPromise(): Promise<MultiBuyerCmp> {
    if (
      this._auth.isLoggedInAsMultiBuyer() &&
      !this._multiBuyerState.selectedMultiBuyerCompany
    ) {
      const user = this._auth.user;
      const primaryEntityId = user?.primary_entity_id;
      if (primaryEntityId) {
        const accessibleIds =
          this._multiBuyerState.accessControlConfig.accessibleEntities.map(
            (e) => e.id
          );
        if (accessibleIds.includes(primaryEntityId)) {
          return this.selectBuyer(user.cmp, primaryEntityId);
        }
      }
    }
    return new NullMultiBuyerCmp();
  }
}
